import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import type { BankruptcyCustomerResultDto } from '../../Containers/BankruptcyTermination.types';
import { BankruptcyOperationStatus } from '../../Containers/BankruptcyTermination.types';
import OperationStatusBadge from './OperationStatusBadge';
import dateUtils from '../../Utils/dateUtils';
import bankruptcyTerminationActions from '../../Actions/bankruptcyTerminationActions';
import { Dropdown as DropdownBase } from '../FormComponents/Dropdown';
import ExpandableTable from '../Fundamentals/Tables/ExpandedableTable';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Dropdown = DropdownBase as any;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ET = ExpandableTable as any;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyDispatch = any;

const CustomerRow = ({ customer }: { customer: BankruptcyCustomerResultDto }) => {
    const { t } = useTranslation();
    const dispatch: AnyDispatch = useDispatch();
    const actions = bindActionCreators(bankruptcyTerminationActions, dispatch);

    const handleManuallyHandledChange = (val: string) => {
        const value = val === '' ? null : val === 'true';
        actions.setManuallyHandled(customer.id, value);
    };

    return (
        <ET.Row>
            <ET.Row.Expandable>
                <td className="text-nowrap">{customer.organizationNumber}</td>
                <td>{customer.customerName}</td>
                <td>{customer.customerNumber}</td>
                <td>{customer.companyStatusCode ?? '-'}</td>
                <td>{customer.companyStatusName ?? '-'}</td>
                <td>{customer.companyStatusDate !== null && customer.companyStatusDate !== undefined ? dateUtils.formatDateAndTime(customer.companyStatusDate) : '-'}</td>
                <td><OperationStatusBadge status={customer.customerUpdateStatus} /></td>
                <td>{customer.successfulSubscriptionCount} / {customer.totalSubscriptionCount}</td>
                <td>{dateUtils.formatDateAndTime(customer.processedAt)}</td>
                <td>
                    {customer.overallStatus === BankruptcyOperationStatus.Skipped
                        ? <span style={{ whiteSpace: 'nowrap' }}>{t('bankruptcytermination.nothandled')}</span>
                        : <OperationStatusBadge status={customer.overallStatus} />}
                </td>
                <td>
                    {customer.hasUnpaidInvoices === null || customer.hasUnpaidInvoices === undefined
                        ? '-'
                        : customer.hasUnpaidInvoices
                            ? <i className="vismaicon vismaicon-filled vismaicon-warning" title={t('bankruptcytermination.yes') ?? undefined} />
                            : <i className="vismaicon vismaicon-filled vismaicon-success" title={t('bankruptcytermination.no') ?? undefined} />
                    }
                </td>
                <td>
                    {customer.hasVbProducts === null || customer.hasVbProducts === undefined
                        ? '-'
                        : customer.hasVbProducts
                            ? <i className="vismaicon vismaicon-filled vismaicon-warning" title={t('bankruptcytermination.yes') ?? undefined} />
                            : <i className="vismaicon vismaicon-filled vismaicon-success" title={t('bankruptcytermination.no') ?? undefined} />
                    }
                </td>
                <td>
                    {customer.emailSendStatus === BankruptcyOperationStatus.Success
                        ? t('bankruptcytermination.sent')
                        : t('bankruptcytermination.notsent')}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                    <Dropdown
                        emptyOption="-"
                        options={[
                            { id: 'true', name: t('bankruptcytermination.yes') },
                            { id: 'false', name: t('bankruptcytermination.no') },
                        ]}
                        value={customer.manuallyHandled === null || customer.manuallyHandled === undefined ? '' : String(customer.manuallyHandled)}
                        onChange={handleManuallyHandledChange}
                    />
                </td>
            </ET.Row.Expandable>
            <ET.Row.Expanded>
                <tr>
                    <td colSpan={14} style={{ background: '#f8f9fa', padding: '12px 24px' }}>
                        {customer.subscriptionResults.length > 0 && (
                            <>
                                <h6>{t('bankruptcytermination.subscriptions')}</h6>
                                <table className="table table-condensed mb-3">
                                    <thead>
                                        <tr>
                                            <th>{t('bankruptcytermination.subscriptionid')}</th>
                                            <th>{t('bankruptcytermination.tier')}</th>
                                            <th>{t('bankruptcytermination.status')}</th>
                                            <th>{t('bankruptcytermination.terminationdate')}</th>
                                            <th>{t('bankruptcytermination.attempt')}</th>
                                            <th>{t('bankruptcytermination.errormessage')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {customer.subscriptionResults.map(sub => (
                                            <tr key={sub.id}>
                                                <td>{sub.subscriptionId}</td>
                                                <td>{sub.tierName}</td>
                                                <td><OperationStatusBadge status={sub.status} /></td>
                                                <td>{sub.terminationDate !== null && sub.terminationDate !== undefined ? dateUtils.formatDateAndTime(sub.terminationDate) : '-'}</td>
                                                <td>{sub.attemptNumber}</td>
                                                <td>{sub.errorMessage ?? '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}
                        {customer.errors.length > 0 && (
                            <>
                                <h6>{t('bankruptcytermination.errors')}</h6>
                                <table className="table table-condensed">
                                    <thead>
                                        <tr>
                                            <th>{t('bankruptcytermination.errormessage')}</th>
                                            <th>{t('bankruptcytermination.createdat')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {customer.errors.map(err => (
                                            <tr key={err.id}>
                                                <td>{err.message}</td>
                                                <td>{dateUtils.formatDateAndTime(err.createdAt)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}
                        {customer.subscriptionResults.length === 0 && customer.errors.length === 0 && (
                            <p className="text-muted mb-0">{t('bankruptcytermination.nodetails')}</p>
                        )}
                    </td>
                </tr>
            </ET.Row.Expanded>
        </ET.Row>
    );
};

export default CustomerRow;
